import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { MapPin, ArrowRight, Loader2, Building2, Search } from "lucide-react";
import { SiteHeader, SiteFooter } from "../components/SiteLayout";
import SEO, { breadcrumb } from "../components/SEO";
import { normalizeBoeAddress } from "../utils/address";
import { SubastaCard } from "./Subastas";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Página índice SEO de una provincia: /subastas/madrid, /subastas/barcelona, etc.
 * El dispatcher en App.js sólo monta este componente si el slug es una
 * provincia válida (lista hardcoded en `utils/provincias.js`).
 */
export default function ProvinciaIndex() {
  const params = useParams();
  // La ruta padre puede ser /subastas/:id o /subastas/:slug. Aceptamos ambos.
  const slug = (params.slug || params.id || "").toLowerCase();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    axios
      .get(`${API}/api/subastas/provincia/${slug}`)
      .then((r) => {
        if (mounted) setData(r.data);
      })
      .catch(() => {
        /* dispatcher ya validó el slug; un fallo aquí es de red */
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [slug]);

  if (loading || !data) {
    return (
      <div className="min-h-screen bg-[#1a110e] text-stone-200 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#E0651F]" />
      </div>
    );
  }

  const { nombre, total_activas, total_terminadas, top_localidades, items } = data;
  const provinciaSlug = slug.toLowerCase();
  const canonical = `/subastas/${provinciaSlug}`;
  const seoTitle = `Subastas BOE en ${nombre} · ${total_activas} inmuebles activos`;
  const seoDesc =
    `Buscador actualizado de subastas inmobiliarias del BOE en ${nombre}: ` +
    `${total_activas} subastas activas y ${total_terminadas} terminadas. ` +
    `Viviendas, locales, garajes, fincas rústicas. Datos oficiales del Portal de Subastas, ` +
    `actualizados dos veces por semana. Asesoría profesional disponible.`;

  return (
    <div className="min-h-screen bg-[#1a110e] text-stone-200">
      <SEO
        title={seoTitle}
        description={seoDesc}
        path={canonical}
        keywords={`subastas BOE ${nombre}, subastas judiciales ${nombre}, subastas AEAT ${nombre}, vivienda subasta ${nombre}, inmuebles subasta ${nombre}`}
        jsonLd={[
          breadcrumb([
            { name: "Inicio", path: "/" },
            { name: "Subastas BOE", path: "/subastas" },
            { name: nombre, path: canonical },
          ]),
          {
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: seoTitle,
            description: seoDesc,
            url: `https://fmsubastas.com${canonical}`,
            isPartOf: { "@type": "WebSite", url: "https://fmsubastas.com" },
            about: {
              "@type": "Place",
              name: nombre,
              address: { "@type": "PostalAddress", addressRegion: nombre, addressCountry: "ES" },
            },
            mainEntity: {
              "@type": "ItemList",
              numberOfItems: total_activas,
              itemListElement: (items || []).slice(0, 12).map((it, i) => ({
                "@type": "ListItem",
                position: i + 1,
                url: `https://fmsubastas.com${it.url_seo || `/subastas/${it.slug || it.identificador}`}`,
                name:
                  (it.subtipo_nombre || "Inmueble") +
                  (it.detalle?.bien?.direccion ? ` en ${normalizeBoeAddress(it.detalle.bien.direccion)}` : ""),
              })),
            },
          },
        ]}
      />
      <SiteHeader />

      {/* Hero */}
      <header className="px-6 pt-12 pb-10 max-w-6xl mx-auto" data-testid="provincia-hero">
        <div className="text-[10px] tracking-[0.3em] uppercase text-[#E0651F] font-bold mb-3 inline-flex items-center gap-2">
          <MapPin className="w-3 h-3" /> Provincia
        </div>
        <h1
          className="font-display font-black text-4xl sm:text-5xl lg:text-6xl text-white leading-tight"
          data-testid="provincia-h1"
        >
          Subastas BOE en <span className="text-[#E0651F]">{nombre}</span>
        </h1>
        <p className="mt-4 text-stone-300 text-base sm:text-lg max-w-3xl">
          Listado actualizado del Portal de Subastas del BOE para {nombre}.
          Hoy hay <strong className="text-white">{total_activas} subastas activas</strong>
          {total_terminadas > 0 && (
            <> y {total_terminadas.toLocaleString("es-ES")} ya finalizadas como referencia histórica</>
          )}
          . Filtramos viviendas, locales, garajes, trasteros y fincas rústicas. Asesoría profesional
          para inversores y particulares interesados en pujar.
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          <Link
            to={`/subastas?provincia=${data.codigo}`}
            className="inline-flex items-center gap-2 bg-[#E0651F] hover:bg-[#c95817] text-white text-sm font-bold px-5 py-2.5 rounded-sm tracking-wide transition-colors"
            data-testid="provincia-search-cta"
          >
            <Search className="w-4 h-4" /> Buscar y filtrar todas las subastas de {nombre}
          </Link>
          <Link
            to="/orientacion"
            className="inline-flex items-center gap-2 border border-stone-700 hover:border-[#E0651F]/60 text-stone-200 hover:text-white text-sm font-medium px-5 py-2.5 rounded-sm tracking-wide transition-colors"
          >
            ¿Te interesa una? Solicita orientación (15 €)
          </Link>
        </div>
      </header>

      {/* Top localidades (linking interno) */}
      {top_localidades && top_localidades.length > 0 && (
        <section className="px-6 pb-10 max-w-6xl mx-auto" data-testid="provincia-localidades">
          <h2 className="text-[10px] tracking-[0.3em] uppercase text-stone-500 font-bold mb-3">
            Municipios con más subastas en {nombre}
          </h2>
          <div className="flex flex-wrap gap-2">
            {top_localidades.map((loc) => (
              <Link
                key={loc.value}
                to={`/subastas?provincia=${data.codigo}&localidad=${encodeURIComponent(loc.value)}`}
                className="inline-flex items-center gap-2 text-xs bg-stone-900 hover:bg-stone-800 border border-stone-800 hover:border-[#E0651F]/40 px-3 py-1.5 rounded-sm transition-colors"
                data-testid={`provincia-localidad-${loc.value}`}
              >
                <Building2 className="w-3 h-3 text-stone-500" />
                <span className="text-stone-200">{loc.label}</span>
                <span className="text-stone-500">· {loc.n}</span>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Listado */}
      <section className="px-6 pb-16 max-w-6xl mx-auto" data-testid="provincia-listado">
        <div className="flex items-baseline justify-between mb-5">
          <h2 className="text-white font-display font-black text-2xl">
            Últimas subastas en {nombre}
          </h2>
          <Link
            to={`/subastas?provincia=${data.codigo}`}
            className="text-[#E0651F] hover:underline text-xs tracking-wide inline-flex items-center gap-1"
            data-testid="provincia-ver-todas"
          >
            Ver las {total_activas} →
          </Link>
        </div>
        {items && items.length > 0 ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {items.map((s) => (
              <SubastaCard key={s.identificador} s={s} />
            ))}
          </div>
        ) : (
          <div className="text-stone-500 text-sm border border-stone-800 rounded-sm px-4 py-8 text-center">
            Ahora mismo no hay subastas activas en {nombre}. Los datos se actualizan a diario.{" "}
            <Link to="/subastas" className="text-[#E0651F] hover:underline">
              Explorar todas las provincias
            </Link>
          </div>
        )}
      </section>

      {/* CTA inferior */}
      <section className="px-6 pb-20 max-w-4xl mx-auto" data-testid="provincia-cta-inferior">
        <div className="bg-stone-900 border border-stone-800 rounded-sm p-6 sm:p-8">
          <h2 className="text-white font-display font-black text-xl sm:text-2xl mb-3">
            ¿Vas a pujar por un inmueble en {nombre}?
          </h2>
          <p className="text-stone-300 text-sm sm:text-base mb-5">
            Antes de comprometer dinero al BOE, asegúrate de que conoces todas las cargas, deudas
            y la situación registral real. Te ayudamos con análisis profesional desde 15 €.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/orientacion"
              className="inline-flex items-center gap-2 bg-[#E0651F] hover:bg-[#c95817] text-white text-sm font-bold px-5 py-2.5 rounded-sm transition-colors"
            >
              Orientación inicial · 15 € <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/informe-premium"
              className="inline-flex items-center gap-2 border border-stone-700 hover:border-[#E0651F]/60 text-stone-200 hover:text-white text-sm font-medium px-5 py-2.5 rounded-sm transition-colors"
            >
              Informe Premium completo · desde 135 €
            </Link>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
